import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  InputBase,
  Button,
  Typography,
  TextField,
  Tooltip,
  Popover,
  Select,
  MenuItem,
  Menu,
  Checkbox
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import SwapVertOutlinedIcon from "@mui/icons-material/SwapVertOutlined";
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * StandardSearchFilterSort Component
 * 
 * A comprehensive search, filter, and sort component that provides a unified interface for data manipulation.
 * 
 * Features:
 * - Search functionality with expandable search box
 * - Filter system with multiple filter options and active filter display
 * - Sorting capability with ascending/descending toggle
 * - Responsive design with hover states and animations
 * 
 * @component
 * @param {Object} props
 * @param {Array} props.columns - Array of column definitions with properties:
 *   @param {string} field - Unique identifier for the column
 *   @param {string} label - Display label for the column
 *   @param {boolean} [filterable=true] - Whether the column can be filtered
 *   @param {boolean} [sortable=true] - Whether the column can be sorted
 *   @param {boolean} [searchable=true] - Whether the column is included in search
 *   @param {'select'|'text'} [filterType] - Type of filter input to use
 *   @param {Array} [filterOptions] - Options for select-type filters
 * @param {function} props.onSearch - Callback when search value changes
 * @param {function} props.onFilter - Callback when filters are applied
 * @param {function} props.onSort - Callback when sort is applied
 * @param {boolean} [props.hiddenMode=false] - Whether to hide the search box
 * @param {HTMLElement} props.filterAnchorEl - Anchor element for filter popover
 * @param {string} props.selectedFilterField - Currently selected filter field
 * @param {function} props.onFilterClick - Callback when filter button is clicked
 * @param {function} props.onFilterClose - Callback when filter popover is closed
 * @param {function} props.onFilterClear - Callback when a filter is cleared
 * @param {function} props.onFilterClearAll - Callback when all filters are cleared
 * @param {Object} props.activeFilters - Currently active filters
 * @param {string} props.searchQuery - Current search query
 * @param {function} props.onClearSearch - Callback when search is cleared
 * @param {Object} props.defaultSort - Default sort configuration
 * 
 * @example
 * ```jsx
 * const columns = [
 *   { field: 'name', label: 'Name', filterable: true, sortable: true },
 *   { field: 'age', label: 'Age', filterType: 'select', filterOptions: [...] }
 * ];
 * 
 * <StandardSearchFilterSort
 *   columns={columns}
 *   onSearch={(value) => console.log('Search:', value)}
 *   onFilter={(filters) => console.log('Filters:', filters)}
 *   onSort={(sort) => console.log('Sort:', sort)}
 *   activeFilters={{}}
 *   searchQuery=""
 *   onClearSearch={() => console.log('Clear search')}
 *   defaultSort={{ field: 'name', direction: 'asc' }}
 * />
 * ```
 */
const StandardSearchFilterSort = ({
  columns,
  onSearch,
  onFilter,
  onSort,
  hiddenMode = false,
  hideSearch = false,
  filterAnchorEl,
  selectedFilterField,
  onFilterClick,
  onFilterClose,
  onFilterClear,
  onFilterClearAll,
  activeFilters,
  searchQuery,
  onClearSearch,
  defaultSort,
  searchHints,
  // Shrinks the always-visible search/filter/sort row (icons + labels + hit
  // targets) to roughly half size. Used when this row is rendered inside the
  // narrow StandardFloatingToolbar panel so it fits without wrapping/overlap.
  // Popover contents (the Filters/Sort dropdown panels) are left full-size
  // since they render as an overlay, not constrained by the panel width.
  dense = false,
}) => {
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [sortConfig, setSortConfig] = useState(defaultSort || {
    field: null,
    direction: 'asc'
  });
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [showAllSort, setShowAllSort] = useState(false);
  const [inlineFilterOpen, setInlineFilterOpen] = useState(false);
  const [inlineSortOpen, setInlineSortOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [autocompleteIndex, setAutocompleteIndex] = useState(0);
  const inputRef = useRef(null);

  const selectMenuProps = {
    disablePortal: dense,
    PaperProps: {
      sx: {
        maxHeight: 300,
        ...(dense ? { zIndex: 1301 } : {}),
      },
    },
  };

  const closeFilterPanels = () => {
    setInlineFilterOpen(false);
    onFilterClose?.();
  };

  const handleFilterButtonClick = (event) => {
    if (dense) {
      setInlineFilterOpen((prev) => {
        const next = !prev;
        if (!next) onFilterClose?.();
        return next;
      });
      setInlineSortOpen(false);
      return;
    }
    onFilterClick(event);
  };

  const handleSortButtonClick = (event) => {
    if (dense) {
      setInlineSortOpen((prev) => !prev);
      setInlineFilterOpen(false);
      return;
    }
    handleSortClick(event);
  };

  useEffect(() => {
    if (dense && filterAnchorEl) {
      setInlineFilterOpen(true);
      setInlineSortOpen(false);
    }
  }, [dense, filterAnchorEl, selectedFilterField]);

  // Add useEffect to sync with external sort changes
  useEffect(() => {
    if (defaultSort) {
      setSortConfig(defaultSort);
    }
  }, [defaultSort]);

  // Keep autocomplete in sync when search is cleared from outside (e.g. parent / URL)
  useEffect(() => {
    if (searchQuery === '' || !searchQuery) {
      setAutocompleteOptions([]);
      setAutocompleteIndex(0);
    }
  }, [searchQuery]);

  // Get only the filterable, sortable, and searchable columns
  const filterableColumns = columns?.filter(column => column.filterable !== false);
  const sortableColumns = columns?.filter(column => column.sortable !== false);
  const searchableColumns = columns?.filter(column => column.searchable !== false);

  const getMultiSelectValue = (field) => {
    const value = activeFilters[field];
    if (Array.isArray(value)) return value;
    if (value === undefined || value === null || value === '') return [];
    return String(value).split(',').filter(Boolean);
  };

  // Sort handlers
  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSort = (field) => {
    const newSortConfig = {
      field,
      direction: sortConfig.field === field && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    };
    setSortConfig(newSortConfig);
    onSort(newSortConfig);
  };

  const handleSortClear = () => {
    const newSortConfig = { field: null, direction: 'asc' };
    setSortConfig(newSortConfig);
    onSort(newSortConfig);
    handleSortClose();
  };

  // Derive field names list from searchHints for autocomplete
  const searchFieldNames = searchHints
    ? searchHints.map(h => h.example.split(':')[0].trim())
    : [];

  // Compute autocomplete options based on the current (last) segment being typed
  const computeAutocomplete = (value) => {
    if (!searchHints || searchHints.length === 0) return [];
    const commaIdx = value.lastIndexOf(',');
    const currentSegment = commaIdx >= 0
      ? value.slice(commaIdx + 1).trimStart()
      : value;
    // Only suggest if the current segment has no colon yet (still typing field name)
    if (currentSegment.includes(':')) return [];
    const q = currentSegment.toLowerCase().trim();
    if (!q) return [];
    return searchFieldNames.filter(f => f.toLowerCase().startsWith(q));
  };

  // Search handler
  const handleSearchChange = (event) => {
    const value = event.target.value;
    onSearch(value, searchableColumns);
    const opts = computeAutocomplete(value);
    setAutocompleteOptions(opts);
    setAutocompleteIndex(0);
  };

  // When user picks a field name from the autocomplete dropdown
  const handleSelectAutocomplete = (fieldName) => {
    const commaIdx = searchQuery.lastIndexOf(',');
    const base = commaIdx >= 0 ? searchQuery.slice(0, commaIdx + 1) + ' ' : '';
    const newQuery = base + fieldName + ': ';
    onSearch(newQuery, searchableColumns);
    setAutocompleteOptions([]);
    setAutocompleteIndex(0);
    // Re-focus the input so the user can type the value immediately
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  // Enter accepts highlighted autocomplete; arrow keys to move selection; Escape to dismiss
  const handleSearchKeyDown = (event) => {
    if (autocompleteOptions.length === 0) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      const name = autocompleteOptions[autocompleteIndex] ?? autocompleteOptions[0];
      if (name) {
        handleSelectAutocomplete(name);
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setAutocompleteIndex((i) => Math.min(i + 1, autocompleteOptions.length - 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setAutocompleteIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setAutocompleteOptions([]);
    }
  };

  // Filter handlers
  const handleFilterApply = (field, value, event) => {
    const column = filterableColumns?.find(col => col.field === field);
    
    // Only prevent empty values for select type filters
    if (column?.filterType === 'select' && value === undefined) return;

    // Handle multi-select differently
    if (column?.filterType === 'multi-select') {
      const newFilters = {
        ...activeFilters
      };

      // Initialize as array if not already
      if (!Array.isArray(newFilters[field])) {
        newFilters[field] = [];
      }

      // If value is empty array (all deselected) or empty string, remove the filter
      if (!value || (Array.isArray(value) && value.length === 0)) {
        delete newFilters[field];
      } else if (Array.isArray(value)) {
        // If value is an array (from Select component), use it directly
        newFilters[field] = value;
      } else {
        // Toggle single value
        const index = newFilters[field].indexOf(value);
        if (index > -1) {
          newFilters[field].splice(index, 1);
          if (newFilters[field].length === 0) {
            delete newFilters[field];
          }
        } else {
          newFilters[field].push(value);
        }
      }

      onFilter(newFilters);
      return;
    }
    
    // Regular single-value filters
    const newFilters = {
      ...activeFilters
    };

    // Special handling for boolean values
    if (value === false || value || value === "" || value === 0) {
      // For boolean false, explicitly assign it
      if (value === false) {
        newFilters[field] = false;
      } else {
        newFilters[field] = value;
      }
    } else {
      delete newFilters[field];
    }

    onFilter(newFilters);
    
    // Only close popover on Enter key press for text inputs
    if (event?.key === 'Enter' && column?.filterType !== 'select' && column?.filterType !== 'multi-select') {
      closeFilterPanels();
    }
  };

  // Common styles that can be reused
  const commonButtonStyles = {
    display: 'flex',
    alignItems: 'center',
    padding: dense ? '5px 10px' : '6px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: dense ? '7px' : '8px',
    minWidth: dense ? "101px" : "120px",
    height: dense ? "34px" : "36px",
    transition: "all 0.3s ease",
    '&:hover': {
      backgroundColor: '#EBEEFF',
      "& .buttonText": {
        color: "#1D3DFC"
      },
      "& .MuiSvgIcon-root": {
        color: "#1D3DFC"
      }
    }
  };

  const commonIconStyles = {
    fontSize: dense ? 17 : 20,
    marginRight: dense ? '7px' : '8px',
    transition: 'color 0.3s ease'
  };

  const commonBadgeStyles = {
    ml: dense ? 0.5 : 1,
    backgroundColor: '#1D3DFC',
    color: 'white',
    borderRadius: '50%',
    width: dense ? '17px' : '20px',
    height: dense ? '17px' : '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: dense ? '10px' : '12px',
    fontWeight: '500'
  };

  const commonTooltipProps = {
    arrow: true,
    placement: "bottom-start",
    componentsProps: {
      tooltip: {
        sx: {
          bgcolor: 'white',
          '& .MuiTooltip-arrow': {
            color: 'white'
          },
          p: 0,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          '& .MuiTypography-root': {
            color: '#374151'
          }
        }
      }
    }
  };

  // Common text styles
  const commonTextStyles = {
    button: {
      fontSize: dense ? "12px" : "14px",
      fontWeight: 500,
      transition: 'color 0.3s ease'
    },
    popoverHeader: {
      fontSize: '14px',
      fontWeight: 600,
      color: '#1D3DFC'
    },
    popoverLabel: {
      fontSize: '13px',
      color: '#6B7280',
      fontWeight: 400
    },
    popoverValue: {
      fontSize: '13px',
      color: '#1D3DFC',
      fontWeight: 500
    },
    clearButton: {
      textTransform: 'none',
      color: '#6B7280',
      fontSize: '13px',
      '&:hover': {
        backgroundColor: 'transparent',
        color: '#1D3DFC'
      }
    }
  };

  const commonPopoverHeaderStyles = {
    display: 'flex', 
    alignItems: 'center', 
    mb: 1.5,
    pb: 1,
    borderBottom: '1px solid rgba(29, 61, 252, 0.2)'
  };

  const commonPopoverTitleStyles = {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1D3DFC'
  };

  const commonPopoverItemStyles = {
    backgroundColor: '#EBEEFF',
    borderRadius: '4px',
    p: '4px 8px',
    display: 'inline-block',
    flex: '0 0 auto'
  };

  const commonShowMoreButtonStyles = {
    textTransform: 'none',
    color: '#1D3DFC',
    mt: 1,
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline'
    }
  };

  return (
    <Box>
      {/* Top Row: Search and Buttons */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        {/* Search Box */}
        <Box
          sx={{ position: 'relative', marginRight: dense ? '10px' : '8px' }}
          style={hiddenMode || hideSearch ? { display: 'none' } : {}}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: dense ? "0 7px" : "0 8px",
              width: dense ? "152px" : "180px",
              height: dense ? "30px" : "36px",
              transition: "width 0.3s ease",
              '& .MuiInputBase-root': {
                width: '100%'
              },
              '&:focus-within': {
                width: dense ? "254px" : "300px",
                borderColor: "#1D3DFC"
              }
            }}
          >
            <SearchOutlinedIcon sx={{ color: '#6B7280', fontSize: dense ? 17 : 20 }} />
            <InputBase
              placeholder={searchableColumns?.length > 0 ? "Search…" : "Search disabled"}
              inputProps={{ "aria-label": "search" }}
              sx={{ 
                fontSize: dense ? "0.74rem" : "0.875rem", 
                padding: dense ? "1px 4px" : "2px 4px", 
                flex: 1,
                "& input": {
                  "&::placeholder": {
                    color: searchableColumns?.length > 0 ? '#9CA3AF' : '#D1D5DB',
                  }
                }
              }}
            value={searchQuery}
            onChange={handleSearchChange}
            disabled={searchableColumns?.length === 0}
            inputRef={inputRef}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => {
              // Delay hiding so a click on an autocomplete option can fire first
              setTimeout(() => {
                setSearchFocused(false);
                setAutocompleteOptions([]);
              }, 150);
            }}
            onKeyDown={handleSearchKeyDown}
            />
            {searchQuery && (
              <IconButton
                type="button"
                size="small"
                aria-label="Clear search"
                onMouseDown={(e) => {
                  // Prevents input blur before click; avoids lost "clear" and odd focus/expand
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setAutocompleteOptions([]);
                  setAutocompleteIndex(0);
                  onClearSearch();
                }}
                sx={{
                  padding: '2px',
                  color: '#6B7280',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: '#1D3DFC'
                  }
                }}
              >
                <ClearIcon sx={{ fontSize: dense ? 13 : 16 }} />
              </IconButton>
            )}
            {searchHints && searchHints.length > 0 && (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, color: '#F9FAFB' }}>
                      Field search shortcuts
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: '#9CA3AF', mb: 0.5, fontSize: '0.7rem' }}>
                      Combine: <Box component="span" sx={{ fontFamily: 'monospace', color: '#D1D5DB' }}>Policy: X, Remarks: Y</Box>
                    </Typography>
                    {searchHints.map((hint, i) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', color: '#D1D5DB', lineHeight: 1.6 }}>
                        {hint.example}
                      </Typography>
                    ))}
                  </Box>
                }
                placement="bottom-start"
                arrow
                enterDelay={200}
                componentsProps={{
                  tooltip: { sx: { maxWidth: 420, bgcolor: '#374151' } },
                }}
              >
                <IconButton
                  type="button"
                  size="small"
                  aria-label="Field search help"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => inputRef.current?.focus()}
                  sx={{
                    padding: '2px',
                    color: '#9CA3AF',
                    flexShrink: 0,
                    '&:hover': { color: '#1D3DFC', backgroundColor: 'transparent' },
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: dense ? 13 : 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Auto-show hints panel OR autocomplete suggestions */}
          {searchHints && searchHints.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: 0,
                zIndex: 1300,
                backgroundColor: '#374151',
                borderRadius: '6px',
                padding: autocompleteOptions.length > 0 ? '6px 0' : '10px 14px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                opacity: searchFocused && (autocompleteOptions.length > 0 || !searchQuery) ? 1 : 0,
                pointerEvents: searchFocused && (autocompleteOptions.length > 0 || !searchQuery) ? 'auto' : 'none',
                transition: 'opacity 0.2s ease',
                whiteSpace: 'nowrap',
                minWidth: '240px',
              }}
            >
              {autocompleteOptions.length > 0 ? (
                // Autocomplete mode: show matching field names
                autocompleteOptions.map((name, i) => (
                  <Box
                    key={i}
                    onMouseEnter={() => setAutocompleteIndex(i)}
                    onMouseDown={(e) => { e.preventDefault(); handleSelectAutocomplete(name); }}
                    sx={{
                      padding: '6px 14px',
                      cursor: 'pointer',
                      color: '#F9FAFB',
                      fontSize: '0.8125rem',
                      fontFamily: 'monospace',
                      backgroundColor: i === autocompleteIndex ? 'rgba(255,255,255,0.15)' : 'transparent',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                    }}
                  >
                    {name}:{' '}
                    <Box component="span" sx={{ color: '#9CA3AF', fontSize: '0.75rem' }}>
                      {searchHints.find(h => h.example.startsWith(name + ':'))?.example.split(':').slice(1).join(':').trim() || '…'}
                    </Box>
                  </Box>
                ))
              ) : (
                // Hints mode: show all shortcuts when bar is empty
                <>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.75, color: '#F9FAFB' }}>
                    Field search shortcuts
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: '#9CA3AF', mb: 0.5, fontSize: '0.7rem' }}>
                    Combine with commas: <Box component="span" sx={{ fontFamily: 'monospace', color: '#D1D5DB' }}>Policy: X, Remarks: Y</Box>
                  </Typography>
                  {searchHints.map((hint, i) => (
                    <Box
                      key={i}
                      onMouseDown={(e) => { e.preventDefault(); handleSelectAutocomplete(hint.example.split(':')[0].trim()); }}
                      sx={{
                        cursor: 'pointer',
                        padding: '1px 0',
                        borderRadius: '2px',
                        '&:hover': { '& .hint-text': { color: '#F9FAFB' } },
                      }}
                    >
                      <Typography className="hint-text" variant="caption" sx={{ display: 'block', fontFamily: 'monospace', color: '#D1D5DB', lineHeight: 1.7, transition: 'color 0.15s' }}>
                        {hint.example}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}
            </Box>
          )}
        </Box>

        {/* Filter Button */}
        {filterableColumns?.length > 0 && (
            <Box
              onClick={handleFilterButtonClick}
              sx={{
                ...commonButtonStyles,
                backgroundColor: (Object.keys(activeFilters).length > 0 || (dense && inlineFilterOpen))
                  ? '#EBEEFF'
                  : 'transparent',
              }}
            >
              <FilterAltOutlinedIcon 
                sx={{ 
                  ...commonIconStyles,
                  color: Object.keys(activeFilters).length > 0 ? '#1D3DFC' : '#6B7280'
                }} 
              />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography 
                  className="buttonText"
                  sx={{ 
                    ...commonTextStyles.button,
                    color: Object.keys(activeFilters).length > 0 ? '#1D3DFC' : '#374151'
                  }}
                >
                  Filters
                </Typography>
                {Object.keys(activeFilters).length > 0 && (
                  <Box sx={commonBadgeStyles}>
                    {Object.keys(activeFilters).length}
                  </Box>
                )}
              </Box>
            </Box>
        )}

        {/* Sort Button */}
        {sortableColumns?.length > 0 && (
            <Box 
              onClick={handleSortButtonClick}
              sx={{
                ...commonButtonStyles,
                backgroundColor: (sortConfig.field || (dense && inlineSortOpen)) ? "#EBEEFF" : "transparent"
              }}
            >
              <SwapVertOutlinedIcon 
                sx={{ 
                  ...commonIconStyles,
                  color: sortConfig.field ? "#1D3DFC" : "#6B7280",
                  transform: sortConfig.direction === 'desc' ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.3s ease, color 0.3s ease'
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography 
                  className="buttonText"
                  sx={{ 
                    ...commonTextStyles.button,
                    color: sortConfig.field ? "#1D3DFC" : "#374151"
                  }}
                >
                  Sort
                </Typography>
                {sortConfig.field && (
                  <Box sx={commonBadgeStyles}>
                    {sortConfig.direction === 'asc' ? '↑' : '↓'}
                  </Box>
                )}
              </Box>
            </Box>
        )}
      </Box>

      {/* Inline filter panel — used inside floating toolbar (dense) to avoid nested popover + transform bugs */}
      {dense && inlineFilterOpen && filterableColumns?.length > 0 && (
        <Box
          sx={{
            mt: 1,
            p: '16px',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            bgcolor: '#FFFFFF',
          }}
        >
          {selectedFilterField ? (
            (() => {
              const column = filterableColumns?.find((col) => col.field === selectedFilterField);
              if (!column) return null;
              return column.filterType === 'select' ? (
                <Box>
                  {column.filterOptions?.map((option) => (
                    <Box
                      key={option.value}
                      onClick={() => {
                        const actualValue =
                          option.value === true ? true :
                          option.value === false ? false :
                          option.value;
                        handleFilterApply(column.field, actualValue);
                        closeFilterPanels();
                      }}
                      sx={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        backgroundColor:
                          (option.value === true && activeFilters[column.field] === true) ||
                          (option.value === false && activeFilters[column.field] === false) ||
                          activeFilters[column.field] === option.value
                            ? '#EBEEFF'
                            : 'transparent',
                        '&:hover': { backgroundColor: '#EBEEFF', color: '#1D3DFC' },
                      }}
                    >
                      <Typography sx={{ fontSize: '14px' }}>{option.label}</Typography>
                    </Box>
                  ))}
                </Box>
              ) : null;
            })()
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 2, borderBottom: '1px solid #E5E7EB' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FilterAltOutlinedIcon sx={{ fontSize: 20, mr: 1, color: '#6B7280' }} />
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>Filters</Typography>
                </Box>
                {Object.keys(activeFilters).length > 0 && (
                  <Button size="small" onClick={onFilterClearAll} sx={{ textTransform: 'none', color: '#6B7280' }}>
                    Clear all
                  </Button>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filterableColumns?.slice(0, showAllFilters ? undefined : 5).map((column) => (
                  <Box key={column.field} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#374151', width: '120px', flex: '0 0 120px' }}>
                      {column.label}
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      {column.filterType === 'select' ? (
                        <Select
                          data-field={column.field}
                          fullWidth
                          size="small"
                          value={
                            activeFilters[column.field] !== undefined
                              ? typeof activeFilters[column.field] === 'boolean'
                                ? String(activeFilters[column.field])
                                : activeFilters[column.field]
                              : ''
                          }
                          onChange={(e) => handleFilterApply(column.field, e.target.value)}
                          displayEmpty
                          MenuProps={selectMenuProps}
                          sx={{
                            backgroundColor: '#F9FAFB',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                          }}
                        >
                          <MenuItem value="">
                            <Typography sx={{ color: '#9CA3AF' }}>Select {column.label}</Typography>
                          </MenuItem>
                          {column.filterOptions?.map((option) => (
                            <MenuItem
                              key={typeof option.value === 'boolean' ? String(option.value) : option.value}
                              value={typeof option.value === 'boolean' ? String(option.value) : option.value}
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <TextField
                          data-field={column.field}
                          fullWidth
                          size="small"
                          placeholder={`Filter by ${column.label}`}
                          value={activeFilters[column.field] ?? ''}
                          onChange={(e) => handleFilterApply(column.field, e.target.value)}
                          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#F9FAFB' } }}
                        />
                      )}
                    </Box>
                  </Box>
                ))}
                {filterableColumns?.length > 5 && (
                  <Button
                    onClick={() => setShowAllFilters(!showAllFilters)}
                    sx={{ textTransform: 'none', color: '#1D3DFC', alignSelf: 'flex-start' }}
                  >
                    {showAllFilters ? 'Show less' : `Show ${filterableColumns.length - 5} more`}
                  </Button>
                )}
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Inline sort panel — dense / floating toolbar mode */}
      {dense && inlineSortOpen && sortableColumns?.length > 0 && (
        <Box
          sx={{
            mt: 1,
            p: '16px',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            bgcolor: '#FFFFFF',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, borderBottom: '1px solid #E5E7EB', pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SwapVertOutlinedIcon sx={{ fontSize: 20, mr: 1, color: '#6B7280' }} />
              <Typography sx={commonTextStyles.popoverHeader}>Sort</Typography>
            </Box>
            {sortConfig.field && (
              <Button size="small" onClick={handleSortClear} sx={commonTextStyles.clearButton}>Clear</Button>
            )}
          </Box>
          {sortableColumns?.slice(0, showAllSort ? undefined : 7).map((column) => (
            <Box
              key={column.field}
              onClick={() => handleSort(column.field)}
              sx={{
                p: 1.5,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderRadius: '4px',
                '&:hover': { backgroundColor: '#F9FAFB' },
                ...(sortConfig.field === column.field && { backgroundColor: '#F3F4F6' }),
              }}
            >
              <Typography sx={{ fontSize: '14px', color: '#374151' }}>{column.label}</Typography>
              {sortConfig.field === column.field && (
                <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>
                  {sortConfig.direction === 'asc' ? 'A to Z' : 'Z to A'}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Filter Popover */}
      {!dense && filterableColumns?.length > 0 && (
        <Popover
          open={Boolean(filterAnchorEl)}
          anchorEl={filterAnchorEl}
          onClose={closeFilterPanels}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: '500px',
              minWidth: '500px',
              maxWidth: '90vw',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
              borderRadius: '8px',
              mt: 1
            }
          }}
          disableRestoreFocus
        >
          <Box sx={{ p: '16px' }}>
            {selectedFilterField ? (
              // Show only the dropdown/input when editing an active filter
              (() => {
                const column = filterableColumns?.find(col => col.field === selectedFilterField);
                if (!column) return null;

                return column.filterType === 'select' ? (
                  <Box>
                    {column.filterOptions?.map((option) => (
                      <Box
                        key={option.value}
                        onClick={() => {
                          const actualValue = 
                            option.value === true ? true :
                            option.value === false ? false :
                            option.value;
                          handleFilterApply(column.field, actualValue);
                          closeFilterPanels();
                        }}
                        sx={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          backgroundColor: (() => {
                            // Special handling for comparing values
                            if (option.value === true && activeFilters[column.field] === true) return '#EBEEFF';
                            if (option.value === false && activeFilters[column.field] === false) return '#EBEEFF';
                            if (activeFilters[column.field] === option.value) return '#EBEEFF';
                            return 'transparent';
                          })(),
                          color: (() => {
                            // Special handling for comparing values
                            if (option.value === true && activeFilters[column.field] === true) return '#1D3DFC';
                            if (option.value === false && activeFilters[column.field] === false) return '#1D3DFC';
                            if (activeFilters[column.field] === option.value) return '#1D3DFC';
                            return '#374151';
                          })(),
                          '&:hover': {
                            backgroundColor: '#EBEEFF',
                            color: '#1D3DFC'
                          }
                        }}
                      >
                        <Typography sx={{ fontSize: '14px' }}>
                          {option.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : column.filterType === 'multi-select' ? (
                  <Select
                    data-field={column.field}
                    fullWidth
                    multiple
                    size="small"
                    value={getMultiSelectValue(column.field)}
                    onChange={(e) => {
                      // Directly apply the array of selected values
                      handleFilterApply(column.field, e.target.value);
                    }}
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return <Typography sx={{ color: '#9CA3AF' }}>
                          Select {column.label}
                        </Typography>;
                      }
                      return selected.map(value => {
                        const option = column.filterOptions?.find(opt => opt.value === value);
                        return option?.label || value;
                      }).join(', ');
                    }}
                    MenuProps={selectMenuProps}
                    sx={{
                      backgroundColor: '#F9FAFB',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1D3DFC',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1D3DFC',
                      }
                    }}
                  >
                    {column.filterOptions?.map((option) => (
                      <MenuItem 
                        key={option.value} 
                        value={option.value}
                        sx={{
                          '&.Mui-selected': {
                            backgroundColor: '#EBEEFF',
                            color: '#1D3DFC',
                            '&:hover': {
                              backgroundColor: '#EBEEFF',
                            }
                          }
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={getMultiSelectValue(column.field).includes(option.value)}
                          sx={{
                            color: '#D1D5DB',
                            '&.Mui-checked': {
                              color: '#1D3DFC',
                            },
                            padding: '2px',
                            marginRight: '8px'
                          }}
                        />
                        <Typography sx={{ fontSize: '14px' }}>
                          {option.label}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <TextField
                      data-field={column.field}
                      fullWidth
                      size="small"
                      placeholder={`Filter by ${column.label}`}
                      value={activeFilters[column.field] !== undefined ? 
                        // Special handling for boolean values in dropdown display
                        typeof activeFilters[column.field] === 'boolean' ? 
                          String(activeFilters[column.field]) : 
                          activeFilters[column.field] 
                        : ''}
                      onChange={(e) => handleFilterApply(column.field, e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleFilterApply(column.field, e.target.value, e);
                        }
                      }}
                      variant="outlined"
                      autoFocus
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#F9FAFB',
                          '& fieldset': {
                            borderColor: '#E5E7EB',
                          },
                          '&:hover fieldset': {
                            borderColor: '#1D3DFC',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1D3DFC',
                          }
                        }
                      }}
                    />
                    {activeFilters[column.field] && (
                      <IconButton
                        size="small"
                        onClick={() => handleFilterApply(column.field, '')}
                        sx={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#6B7280',
                          padding: '2px',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            color: '#1D3DFC'
                          }
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Box>
                );
              })()
            ) : (
              // Show regular filter view when clicking the main filter button
              <>
                {/* Header */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2,
                  pb: 2,
                  borderBottom: '1px solid #E5E7EB'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FilterAltOutlinedIcon sx={{ fontSize: 20, mr: 1, color: '#6B7280' }} />
                    <Typography sx={{ 
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151'
                    }}>
                      Filters
                    </Typography>
                  </Box>
                  {Object.keys(activeFilters).length > 0 && (
                    <Button 
                      size="small" 
                      onClick={onFilterClearAll}
                      sx={{
                        textTransform: 'none',
                        color: '#6B7280',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: '#1D3DFC'
                        }
                      }}
                    >
                      Clear all
                    </Button>
                  )}
                </Box>

                {/* Filter Fields */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {(() => {
                    const hasAdvancedFilters = filterableColumns?.some(col => col.filterAdvanced);
                    return filterableColumns?.filter((column, index) => {
                      if (showAllFilters) return true;
                      if (hasAdvancedFilters) return !column.filterAdvanced;
                      return index < 5;
                    });
                  })()
                    .map((column) => (
                    <Box 
                      key={column.field}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                      }}
                    >
                      <Typography sx={{ 
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151',
                        width: '120px',
                        flex: '0 0 120px'
                      }}>
                        {column.label}
                      </Typography>
                      
                      <Box sx={{ flex: 1 }}>
                        {column.filterType === 'multi-select' ? (
                          <Select
                            data-field={column.field}
                            fullWidth
                            multiple
                            size="small"
                            value={getMultiSelectValue(column.field)}
                            onChange={(e) => {
                              // Directly apply the array of selected values
                              handleFilterApply(column.field, e.target.value);
                            }}
                            displayEmpty
                            renderValue={(selected) => {
                              if (selected.length === 0) {
                                return <Typography sx={{ color: '#9CA3AF' }}>
                                  Select {column.label}
                                </Typography>;
                              }
                              return selected.map(value => {
                                const option = column.filterOptions?.find(opt => opt.value === value);
                                return option?.label || value;
                              }).join(', ');
                            }}
                            MenuProps={selectMenuProps}
                            sx={{
                              backgroundColor: '#F9FAFB',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#E5E7EB',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#1D3DFC',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#1D3DFC',
                              }
                            }}
                          >
                            {column.filterOptions?.map((option) => (
                              <MenuItem 
                                key={option.value} 
                                value={option.value}
                                sx={{
                                  '&.Mui-selected': {
                                    backgroundColor: '#EBEEFF',
                                    color: '#1D3DFC',
                                    '&:hover': {
                                      backgroundColor: '#EBEEFF',
                                    }
                                  }
                                }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={getMultiSelectValue(column.field).includes(option.value)}
                                  sx={{
                                    color: '#D1D5DB',
                                    '&.Mui-checked': {
                                      color: '#1D3DFC',
                                    },
                                    padding: '2px',
                                    marginRight: '8px'
                                  }}
                                />
                                <Typography sx={{ fontSize: '14px' }}>
                                  {option.label}
                                </Typography>
                              </MenuItem>
                            ))}
                          </Select>
                        ) : column.filterType === 'select' ? (
                          <Select
                            data-field={column.field}
                            fullWidth
                            size="small"
                            value={activeFilters[column.field] !== undefined ? 
                              // Special handling for boolean values in dropdown display
                              typeof activeFilters[column.field] === 'boolean' ? 
                                String(activeFilters[column.field]) : 
                                activeFilters[column.field] 
                              : ''}
                            onChange={(e) => {
                              // Special handling for boolean values
                              const value = e.target.value;
                              // Preserve boolean type for true/false values
                              if (column.field === 'is_mandatory') {
                                if (value === 'true') handleFilterApply(column.field, true);
                                else if (value === 'false') handleFilterApply(column.field, false);
                                else handleFilterApply(column.field, value);
                              } else {
                                handleFilterApply(column.field, value);
                              }
                            }}
                            displayEmpty
                            MenuProps={selectMenuProps}
                            sx={{
                              backgroundColor: '#F9FAFB',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#E5E7EB',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#1D3DFC',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#1D3DFC',
                              }
                            }}
                          >
                            <MenuItem value="">
                              <Typography sx={{ color: '#9CA3AF' }}>
                                Select {column.label}
                              </Typography>
                            </MenuItem>
                            {column.filterOptions?.map((option) => {
                              // Special handling for boolean values
                              let optionValue = option.value;
                              let isSelected = false;
                              
                              // For boolean values, compare correctly
                              if (optionValue === true || optionValue === false) {
                                isSelected = activeFilters[column.field] === optionValue;
                              } else {
                                isSelected = activeFilters[column.field] === optionValue;
                              }
                              
                              return (
                                <MenuItem 
                                  key={typeof optionValue === 'boolean' ? String(optionValue) : optionValue} 
                                  value={typeof optionValue === 'boolean' ? String(optionValue) : optionValue}
                                  selected={isSelected}
                                  sx={{
                                    backgroundColor: isSelected ? '#EBEEFF' : 'transparent',
                                    '&.Mui-selected': {
                                      backgroundColor: '#EBEEFF',
                                      color: '#1D3DFC'
                                    },
                                    '&:hover': {
                                      backgroundColor: isSelected ? '#EBEEFF' : '#F9FAFB'
                                    }
                                  }}
                                >
                                  {option.label}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        ) : (
                          <Box sx={{ position: 'relative', width: '100%' }}>
                            <TextField
                              data-field={column.field}
                              fullWidth
                              size="small"
                              placeholder={`Filter by ${column.label}`}
                              value={activeFilters[column.field] !== undefined ? 
                                // Special handling for boolean values in dropdown display
                                typeof activeFilters[column.field] === 'boolean' ? 
                                  String(activeFilters[column.field]) : 
                                  activeFilters[column.field] 
                                : ''}
                              onChange={(e) => handleFilterApply(column.field, e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleFilterApply(column.field, e.target.value, e);
                                }
                              }}
                              variant="outlined"
                              autoFocus
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: '#F9FAFB',
                                  '& fieldset': {
                                    borderColor: '#E5E7EB',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: '#1D3DFC',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#1D3DFC',
                                  }
                                }
                              }}
                            />
                            {activeFilters[column.field] && (
                              <IconButton
                                size="small"
                                onClick={() => handleFilterApply(column.field, '')}
                                sx={{
                                  position: 'absolute',
                                  right: '8px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  color: '#6B7280',
                                  padding: '2px',
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                    color: '#1D3DFC'
                                  }
                                }}
                              >
                                <CloseIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}

                  {/* Show More Button */}
                  {(() => {
                    const hasAdvancedFilters = filterableColumns?.some(col => col.filterAdvanced);
                    const showButton = hasAdvancedFilters
                      ? filterableColumns?.some(col => col.filterAdvanced)
                      : filterableColumns?.length > 5;
                    if (!showButton) return null;
                    const moreCount = hasAdvancedFilters
                      ? filterableColumns.filter(c => c.filterAdvanced).length
                      : filterableColumns.length - 5;
                    return (
                      <Button
                        onClick={() => setShowAllFilters(!showAllFilters)}
                        sx={{
                          textTransform: 'none',
                          color: '#1D3DFC',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {showAllFilters ? 'Show less' : `Show ${moreCount} more`}
                      </Button>
                    );
                  })()}
                </Box>
              </>
            )}
          </Box>
        </Popover>
      )}

      {/* Sort Popover */}
      {!dense && sortableColumns?.length > 0 && (
        <Popover
          open={Boolean(sortAnchorEl)}
          anchorEl={sortAnchorEl}
          onClose={handleSortClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: '320px',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
              borderRadius: '8px',
              mt: 1
            }
          }}
        >
          <Box sx={{ p: '16px' }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 3,
              borderBottom: '1px solid #E5E7EB',
              pb: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SwapVertOutlinedIcon sx={{ fontSize: 20, mr: 1, color: '#6B7280' }} />
                <Typography sx={commonTextStyles.popoverHeader}>
                  Sort
                </Typography>
              </Box>
              {sortConfig.field && (
                <Button 
                  size="small" 
                  onClick={handleSortClear}
                  sx={commonTextStyles.clearButton}
                >
                  Clear
                </Button>
              )}
            </Box>
            
            {sortableColumns
              ?.slice(0, showAllSort ? undefined : 7)
              .map((column) => (
              <Box 
                key={column.field}
                onClick={() => handleSort(column.field)}
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#F9FAFB'
                  },
                  ...(sortConfig.field === column.field && {
                    backgroundColor: '#F3F4F6'
                  })
                }}
              >
                <Typography sx={{ 
                  fontSize: '14px',
                  color: '#374151'
                }}>
                  {column.label}
                </Typography>
                {sortConfig.field === column.field && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    color: '#6B7280',
                    fontSize: '14px'
                  }}>
                    {sortConfig.direction === 'asc' ? 'A to Z' : 'Z to A'}
                    <SwapVertOutlinedIcon 
                      sx={{ 
                        fontSize: 18, 
                        ml: 0.5,
                        transform: sortConfig.direction === 'desc' ? 'rotate(180deg)' : 'none'
                      }} 
                    />
                  </Box>
                )}
              </Box>
            ))}

            {/* Show More/Less Button */}
            {sortableColumns?.length > 7 && (
              <Button
                onClick={() => setShowAllSort(!showAllSort)}
                sx={commonShowMoreButtonStyles}
              >
                {showAllSort ? 'Show less' : `Show ${sortableColumns.length - 7} more`}
              </Button>
            )}
          </Box>
        </Popover>
      )}
    </Box>
  );
};

export default StandardSearchFilterSort; 